import React, { useContext, useEffect, useRef } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { BaseContext } from "../../shared/base-context";
import { useStableQuery } from "../../shared/hooks/useStableQuery";
import { useLoadingWrapper } from "../../shared/hooks/useLoadingWrapper";
import { EvergreenMessage } from "./components/EvergreenMessage";
import { EvergreenInput } from "./components/EvergreenInput";
import { AcornIcon } from "../../shared/components/AcornIcon";
import { Busy } from "../chat/components/busy";
import { BoarMascot } from "../../shared/components/BoarMascot";

export const EvergreenChat: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { convexUserId, user } = useContext(BaseContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Ref to track last consumed pending context ID (prevents double-consumption in StrictMode)
  const lastConsumedPendingRef = useRef<string | null>(null);
  // Ref to prevent URL param re-injection (runs once on mount)
  const urlParamsInjectedRef = useRef(false);

  const messages = useStableQuery(
    api.evergreen_chat.getMessages,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  const chatState = useStableQuery(
    api.evergreen_chat.getChatState,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  // Get user metadata for nickname
  const userMetadata = useStableQuery(
    api.user_metadata.getUserMetadata,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  // Get pending context from database
  const pendingContext = useStableQuery(
    api.evergreen_pending_context.getPendingContext,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  // Check acorn balance to gate chat input
  const acornData = useQuery(
    api.acorns.getUserAcorns,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );
  const hasAcorns = !acornData || (acornData.balance ?? 0) > 0;

  const sendMessage = useMutation(api.evergreen_chat.sendMessage);
  const injectContext = useMutation(api.evergreen_chat.injectContext);
  const consumePendingContext = useMutation(api.evergreen_pending_context.consumePendingContext);

  const isLoading = messages === undefined;
  const LoadingWrapper = useLoadingWrapper({ isLoading });

  // Handle pending context from database — reactive so it fires even if chat is already mounted
  useEffect(() => {
    if (!convexUserId) return;
    if (!pendingContext || pendingContext.is_consumed) return;
    // Prevent double-consumption in StrictMode by tracking the last consumed ID
    if (lastConsumedPendingRef.current === pendingContext._id) return;

    lastConsumedPendingRef.current = pendingContext._id;

    // Consume the pending context
    consumePendingContext({
      user_id: convexUserId as Id<"users">,
      context_id: pendingContext._id,
    });

    // Inject the context
    injectContext({
      user_id: convexUserId as Id<"users">,
      context_id: pendingContext.context_id,
      system_message: pendingContext.system_message,
      auto_submit_prompt: pendingContext.auto_submit_prompt,
    });
  }, [convexUserId, pendingContext, consumePendingContext, injectContext]);

  // Handle URL parameter context injection — runs once on mount
  useEffect(() => {
    if (urlParamsInjectedRef.current || !convexUserId) return;

    // Wait for pendingContext query to load; skip if pending context exists (it takes priority)
    if (pendingContext === undefined) return;
    if (pendingContext && !pendingContext.is_consumed) return;

    const contextType = searchParams.get("context");

    // Only inject if there's a context parameter
    if (!contextType) {
      urlParamsInjectedRef.current = true;
      return;
    }

    const skillId = searchParams.get("skillId");
    const skillName = searchParams.get("skillName");
    const sessionName = searchParams.get("sessionName");
    const customPrompt = searchParams.get("prompt");
    const customSystemMessage = searchParams.get("systemMessage");

    let contextId: string | null = null;
    let systemMessage: string | null = null;
    let autoSubmitPrompt: string | undefined = undefined;

    if (contextType === "skill" && skillId && skillName) {
      contextId = `skill_${skillId}`;
      systemMessage = `User is viewing their progress on the "${skillName}" skill. They may ask questions about this skill, their progress, or request recommendations for next sessions within this skill.`;
      autoSubmitPrompt = `Tell me more about my progress on ${skillName}.`;
    } else if (contextType === "session_complete" && sessionName && skillName) {
      contextId = `session_complete_${Date.now()}`;
      systemMessage = `User just completed the "${sessionName}" session in the "${skillName}" skill. Acknowledge their progress and offer guidance on next steps.`;
      autoSubmitPrompt = `I just finished ${sessionName}. What should I do next?`;
    } else if (contextType === "mindset") {
      contextId = "mindset_review";
      systemMessage = "User wants to review their mindset scores and understand their progress.";
      autoSubmitPrompt = "How am I doing overall? Can you explain my scores?";
    } else if (customPrompt) {
      contextId = contextType;
      systemMessage = customSystemMessage || "User opened the chat with a specific question.";
      autoSubmitPrompt = customPrompt;
    }

    // Mark as injected BEFORE making the async call
    urlParamsInjectedRef.current = true;

    if (contextId && systemMessage) {
      injectContext({
        user_id: convexUserId as Id<"users">,
        context_id: contextId,
        system_message: systemMessage,
        auto_submit_prompt: autoSubmitPrompt,
      });
    }
  }, [convexUserId, searchParams, injectContext, pendingContext]);

  // Auto-scroll on new messages or content updates
  useEffect(() => {
    if (messages && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleSendMessage = async (content: string) => {
    if (!convexUserId || !content.trim()) return;

    await sendMessage({
      user_id: convexUserId as Id<"users">,
      content: content.trim(),
      context_id: chatState?.active_context_id,
    });
  };

  const handleQuickReply = (reply: string) => {
    handleSendMessage(reply);
  };

  // Use nickname from user_metadata, then first name from legal_name, then Clerk firstName
  const userName = userMetadata?.nickname || userMetadata?.legal_name?.split(' ')[0] || user?.firstName || "You";

  // Determine if we should show the typing indicator
  const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastMessageEmptyAssistant = lastMessage?.role === "assistant" && !lastMessage?.content;
  const isWaitingForAssistant = lastMessage?.role === "user";
  const showTypingIndicator = chatState?.response_in_progress && (isWaitingForAssistant || isLastMessageEmptyAssistant);

  // Filter out empty assistant messages from display
  const visibleMessages = messages?.filter(m => !(m.role === "assistant" && !m.content)) || [];

  return (
    <LoadingWrapper>
      <section className="min-h-screen bg-background pt-24 pb-[calc(20rem+env(safe-area-inset-bottom))]">
        <div className="max-w-4xl mx-auto px-6">
          {/* Messages */}
          <div className="space-y-6 pb-[calc(8rem+env(safe-area-inset-bottom))]">
            {visibleMessages.length === 0 && !showTypingIndicator && (
              <div className="text-center py-16 animate-fade-in">
                <BoarMascot className="h-20 mb-4" />
                <p className="text-foreground text-xl font-medium mb-2">Welcome to your personal chat!</p>
                <p className="text-muted-foreground">
                  Ask us anything about your career journey, progress, or next steps.
                </p>
              </div>
            )}

            {visibleMessages.map((message, index) => {
              // Only the last message can be streaming
              const isLastMessage = index === visibleMessages.length - 1;
              const isMessageStreaming = isLastMessage && chatState?.response_in_progress && message.role === "assistant";

              return (
                <EvergreenMessage
                  key={message._id}
                  message={message}
                  userName={userName}
                  onQuickReply={handleQuickReply}
                  isStreaming={isMessageStreaming}
                />
              );
            })}

            {/* Typing indicator */}
            {showTypingIndicator && (
              <article className="mr-auto w-full lg:w-2/3 mb-4">
                <div className="font-semibold text-foreground">Gruntled</div>
                <div className="text-foreground"><Busy /></div>
              </article>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input or no-acorns message */}
          {hasAcorns ? (
            <EvergreenInput
              onSend={handleSendMessage}
              disabled={chatState?.response_in_progress}
            />
          ) : (
            <div className="pb-[calc(1rem+env(safe-area-inset-bottom))] fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-background/95 border-t border-border/30 pt-5 pb-5">
              <div className="max-w-md mx-auto px-6">
                <div className="flex items-start gap-3 bg-accent/10 border border-accent/20 rounded-xl px-4 py-3">
                  <AcornIcon size={28} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-foreground font-medium text-sm">
                      {t('chatInput.noAcornsTitle')}
                    </p>
                    <ul className="text-muted-foreground text-xs mt-1.5 space-y-0.5">
                      <li className="flex items-center gap-1.5">
                        <span className="text-primary">&#x2022;</span> {t('chatInput.noAcornsEarnDaily')}
                      </li>
                      <li className="flex items-center gap-1.5">
                        <span className="text-primary">&#x2022;</span> {t('chatInput.noAcornsEarnSessions')}
                      </li>
                      <li className="flex items-center gap-1.5">
                        <span className="text-primary">&#x2022;</span> {t('chatInput.noAcornsCheckIn')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </LoadingWrapper>
  );
};
